import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  MessageSquare, 
  Eye,
  ChevronLeft,
  ChevronRight,
  X,
  Mail,
  Phone,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminContacts = () => {
  const { authFetch } = useAdmin();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const limit = 20;

  useEffect(() => {
    fetchContacts();
  }, [page, statusFilter]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/admin/contacts?skip=${page * limit}&limit=${limit}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const response = await authFetch(url);
      const data = await response.json();
      setContacts(data.contacts);
      setTotal(data.total);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewContact = async (contact) => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/contacts/${contact.message_id}`);
      const data = await response.json();
      setSelectedContact(data);
      setShowModal(true);
      
      // Auto-mark as read
      if (data.status === 'new') {
        updateStatus(contact.message_id, 'read');
      }
    } catch (error) {
      console.error('Error fetching contact:', error);
    }
  };

  const updateStatus = async (messageId, newStatus) => {
    try {
      await authFetch(`${API_URL}/api/admin/contacts/${messageId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      
      fetchContacts();
      if (selectedContact) {
        setSelectedContact({ ...selectedContact, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-pink-100 text-pink-800';
      case 'read': return 'bg-blue-100 text-blue-800';
      case 'replied': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
            <p className="text-gray-600">{total} total messages</p>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500"
            data-testid="status-filter"
          >
            <option value="">All Status</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="replied">Replied</option>
          </select>
        </div>

        {/* Contacts Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No messages found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="contacts-table">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {contacts.map((contact) => (
                      <tr 
                        key={contact.message_id} 
                        className={`hover:bg-gray-50 ${contact.status === 'new' ? 'bg-pink-50' : ''}`}
                      >
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900">{contact.name}</p>
                          <p className="text-sm text-gray-500">{contact.email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-gray-900 line-clamp-1">{contact.subject}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${getStatusColor(contact.status)}`}>
                            {contact.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(contact.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => viewContact(contact)}
                            data-testid={`view-contact-${contact.message_id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <p className="text-sm text-gray-600">
                  Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contact Detail Modal */}
      {showModal && selectedContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-2xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Message Details</CardTitle>
                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full mt-2 ${getStatusColor(selectedContact.status)}`}>
                  {selectedContact.status}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">From</h3>
                  <p className="text-gray-700">{selectedContact.name}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Date</h3>
                  <p className="text-gray-700">{new Date(selectedContact.created_at).toLocaleString()}</p>
                </div>
              </div>

              {/* Contact Methods */}
              <div className="flex flex-wrap gap-3">
                <a 
                  href={`mailto:${selectedContact.email}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-pink-50 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  {selectedContact.email}
                </a>
                {selectedContact.phone && (
                  <a 
                    href={`tel:${selectedContact.phone}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    {selectedContact.phone}
                  </a>
                )}
              </div>

              {/* Subject */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Subject</h3>
                <p className="text-gray-700">{selectedContact.subject}</p>
              </div>

              {/* Message */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Message</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => updateStatus(selectedContact.message_id, 'replied')}
                  disabled={selectedContact.status === 'replied'}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Replied
                </Button>
                <a 
                  href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Reply via Email
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminContacts;
